import { Box, ButtonBase, createStyles, makeStyles, Modal, Slide } from '@material-ui/core'
import React, { useCallback } from 'react'
import { Button, Colors, DynamicMuiIcon, Typography } from 'src/components/atoms'
import { useDeleteDocumentFolder } from 'src/fixtures/modules/documentFolder/hooks'
import { DocumentFolder } from 'src/fixtures/types/folder'

const useStyles = makeStyles(() => {
  return createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.base.black,
      zIndex: 9999
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '400px',
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid #DADCE0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#222B35'
    },
    closeButton: {
      width: '28px',
      height: '28px',
      border: 'none',
      background: 'transparent',
      color: '#707B89',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: '#E3E7ED'
      }
    },
    body: {
      padding: '32px 24px 40px'
    },
    bodyText: {
      fontSize: '14px',
      color: '#222B35',
      lineHeight: 1.6
    },
    errorText: {
      color: '#DE1B63',
      marginTop: '12px'
    },
    footer: {
      padding: '16px 24px',
      borderTop: '1px solid #DADCE0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px'
    }
  })
})

export interface ModalFolderDeleteProps {
  open: boolean
  folder: DocumentFolder | null
  onClose: () => void
  onSuccess?: () => void
}

export const ModalFolderDelete = ({ open, folder, onClose, onSuccess }: ModalFolderDeleteProps) => {
  const classes = useStyles()
  const { handleDeleteFolder, isLoading } = useDeleteDocumentFolder()

  const handleConfirm = useCallback(async () => {
    if (!folder) return

    const result = await handleDeleteFolder(folder.id, folder.documentsCount)
    if (result) {
      onClose()
      onSuccess?.()
    }
  }, [folder, handleDeleteFolder, onClose, onSuccess])

  if (!folder) return null

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Slide in={open} direction="up">
        <Box className={classes.container}>
          {/* ヘッダー */}
          <Box className={classes.header}>
            <Typography className={classes.title}>フォルダの削除</Typography>
            <ButtonBase onClick={onClose} className={classes.closeButton}>
              <DynamicMuiIcon variant="close" size="20px" />
            </ButtonBase>
          </Box>

          {/* コンテンツ */}
          <Box className={classes.body}>
            <Typography className={classes.bodyText}>このフォルダを削除してもよろしいですか？</Typography>
            {folder.documentsCount > 0 && (
              <Typography className={`${classes.bodyText} ${classes.errorText}`}>
                このフォルダには{folder.documentsCount}件の資料が含まれているため削除できません。
              </Typography>
            )}
          </Box>

          {/* フッター */}
          <Box className={classes.footer}>
            <Button kind="neutral" variant="outlined" title="キャンセル" onClick={onClose} />
            <Button
              kind="negative"
              title="削除"
              onClick={handleConfirm}
              disabled={folder.documentsCount > 0}
              loading={isLoading}
            />
          </Box>
        </Box>
      </Slide>
    </Modal>
  )
}
